// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { PLANTING_STATUS, utils } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { getID, getNumericID } from '../../utils';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import HarvestLink from './HarvestLink';

interface PlantingData {
  readonly id: string;
  readonly planting_time: number;
  readonly planting_status: string;
  readonly trays_rounded: string;
  readonly crop?: {
    readonly id: string;
  } | null;
  readonly order_id?: string | null;
}
interface Props {
  readonly harvestGroups: PlantingData[][];
  readonly showOrdersAndStatus?: boolean;
}

export default function HarvestTable({ harvestGroups, showOrdersAndStatus = true }: Props) {
  const idValues: React.ReactElement[] = [];
  const trayCounts: number[] = [];
  const cropCounts: number[] = [];
  const orderCounts: number[] = [];
  const statusValues: string[] = [];

  for (const harvestGroup of harvestGroups) {
    const planting = harvestGroup[0];
    idValues.push(
      <HarvestLink
        id={getID(planting.id)}
        title={<Time time={planting.planting_time} options={{ removeDayOfWeek: false, shortenDayOfMonth: false }} />}
      />,
    );

    // To get tray count we need to sum trays_rounded for each CROP
    // Then do Math.ceil on the tray counts for each crop
    // Then sum all those together
    const plantingsGroupedByCrop = harvestGroup.reduce((acc, p) => {
      const cropId = p.crop?.id;
      if (cropId !== undefined) {
        const numId = getNumericID(cropId);
        const maybePlantings = acc.get(numId);
        if (maybePlantings === undefined) {
          acc.set(numId, { plantings: [p], roundedTraysSummed: Number.parseFloat(p.trays_rounded) });
        } else {
          acc.set(numId, {
            plantings: [p, ...maybePlantings.plantings],
            roundedTraysSummed: maybePlantings.roundedTraysSummed + Number.parseFloat(p.trays_rounded),
          });
        }
      }

      return acc;
    }, new Map<number, { readonly plantings: PlantingData[]; readonly roundedTraysSummed: number }>());
    const plantingsGroupedAndRounded = Array.from(plantingsGroupedByCrop.entries()).reduce((acc, [cropId, group]) => {
      const finalTrayCount = Math.ceil(group.roundedTraysSummed);
      acc.set(cropId, { ...group, finalTrayCount });

      return acc;
    }, new Map<
      number,
      { readonly plantings: PlantingData[]; readonly roundedTraysSummed: number; readonly finalTrayCount: number }
    >());

    const trayCount = Array.from(plantingsGroupedAndRounded.values()).reduce((acc, group) => {
      return acc + group.finalTrayCount;
    }, 0);
    const cropCount = new Set(harvestGroup.map((p) => p.crop?.id).filter(utils.notNull)).size;
    const orderCount = new Set(harvestGroup.map((p) => p.order_id).filter(utils.notNull)).size;
    const status = harvestGroup.every(
      (h) => h.planting_status === PLANTING_STATUS.COMPLETED,
      // (h) => h.planting_status === PLANTING_STATUS.COMPLETED || h.planting_status === PLANTING_STATUS.CANCELLED,
    )
      ? 'Complete'
      : 'Not Complete';

    trayCounts.push(trayCount);
    cropCounts.push(cropCount);
    orderCounts.push(orderCount);
    statusValues.push(status);
  }
  const columns: ColumnType[] = [
    {
      name: 'Harvest Date',
      values: idValues,
    },
    {
      name: 'Trays',
      values: trayCounts,
      numeric: true,
    },
    {
      name: 'Crops',
      values: cropCounts,
      numeric: true,
    },
    showOrdersAndStatus
      ? {
          name: 'Orders',
          values: orderCounts,
          numeric: true,
          cellStyle: {
            paddingRight: {
              xs: 1,
              sm: 2,
              md: 0,
              '@xs': 1,
              '@sm': 2,
              '@md': 0,
            },
          },
        }
      : undefined,
    showOrdersAndStatus
      ? {
          name: 'Status',
          values: statusValues,
          visibleAt: 'md' as const,
        }
      : undefined,
  ].filter(utils.notNull);

  return <Table columns={columns} />;
}
